import { PlusOutlined } from '@ant-design/icons';
import {
  CaptureWorkflowPendingReviewIcon,
  CaptureWorkflowReadyToPostIcon,
} from './capture-workflow-metrics';
import { ToggleButton } from '@monorepo/shared-lib';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ReceiptEntityStatistics } from 'store/organisation/models';
import { clearOrganisationDetailsTabStorage } from 'utils/clear-organisation-tab-storage';
import { getEntityOrganisationPath } from 'utils/get-entity-organisation-path';
import {
  navigateToEntityCaptureTab,
  navigateToEntityTransactionsTab,
} from 'utils/navigate-to-entity-capture';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { buildPwaEntityCaptureNavigateUrl, buildPwaEntityTransactionsNavigateUrl } from 'utils/pwa-nav';
export type EntityCaptureFileCounts = {
  pendingReviewFiles: number;
  readyToPostFiles: number;
};

interface Props {
  connectionId: string;
  organisationId: string;
  providerName?: string;
  stats?: ReceiptEntityStatistics;
  /** When file metadata is loaded, prefer counts that match the Transactions side panel. */
  fileCountsFromMetadata?: EntityCaptureFileCounts;
  hideReadyToPost?: boolean;
  showEnable?: boolean;
}

export const EntityCaptureStatsCell: React.FC<Props> = ({
  organisationId,
  providerName,
  stats,
  fileCountsFromMetadata,
  hideReadyToPost = false,
  showEnable = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const to = getEntityOrganisationPath(organisationId, providerName);
  const transactionsPath = pwaMobileNav
    ? buildPwaEntityTransactionsNavigateUrl(organisationId)
    : `${to}?tab=transactions`;
  const capturePath = pwaMobileNav
    ? buildPwaEntityCaptureNavigateUrl(organisationId)
    : `${to}?tab=capture`;

  if (showEnable) {
    const handleEnableCapture = () => {
      clearOrganisationDetailsTabStorage();
      navigateToEntityCaptureTab();
      navigate(capturePath);
    };

    return (
      <span className="entity-capture-backlog entity-capture-backlog--enable">
        <ToggleButton
          type="primary"
          shape="circle"
          icon={<PlusOutlined />}
          paddinglft="115px"
          onClick={handleEnableCapture}
        >
          <span className="show-on-hover">
            {t('organisation.enable-capture')}
          </span>
        </ToggleButton>
      </span>
    );
  }

  if (!stats) {
    return null;
  }

  const pendingReviewFiles =
    fileCountsFromMetadata?.pendingReviewFiles ?? stats.filesPendingReview;
  const pendingReviewTransactions = stats.transactionsPendingReview;
  const readyToPostFiles =
    fileCountsFromMetadata?.readyToPostFiles ?? stats.filesReviewed;
  const readyToPostTransactions = stats.reviewedTransactions;

  const pendingReviewLabel = t('organisation.transactions-view-tab-pending-review');
  const readyToPostLabel = t('organisation.transactions-view-tab-reviewed');
  const formatCaptureTooltipCounts = (files: number, transactions: number) =>
    t('organisation.capture-statistics-tooltip-counts', { files, transactions });
  const tooltip = (
    <div className="entity-capture-backlog__tooltip">
      <div>
        {pendingReviewLabel}:{' '}
        {formatCaptureTooltipCounts(
          pendingReviewFiles,
          pendingReviewTransactions
        )}
      </div>
      {!hideReadyToPost ? (
        <div>
          {readyToPostLabel}:{' '}
          {formatCaptureTooltipCounts(
            readyToPostFiles,
            readyToPostTransactions
          )}
        </div>
      ) : null}
    </div>
  );

  return (
    <StyledTooltip placement="top" title={tooltip} width="320px">
      <Link
        to={transactionsPath}
        className="entity-capture-backlog entity-capture-backlog--link"
        onClick={() => {
          clearOrganisationDetailsTabStorage();
          navigateToEntityTransactionsTab();
        }}
      >
        <span className="entity-capture-backlog__metrics">
          <span
            className="entity-capture-backlog__metric"
            aria-label={`${pendingReviewLabel}: ${formatCaptureTooltipCounts(
              pendingReviewFiles,
              pendingReviewTransactions
            )}`}
          >
            <span className="entity-capture-backlog__label">
              {t('common.review')}
            </span>
            <CaptureWorkflowPendingReviewIcon />
            <span className="entity-capture-backlog__value">
              {formatDisplayInteger(pendingReviewFiles, i18n.language)}
            </span>
          </span>
          {!hideReadyToPost ? (
            <span
              className="entity-capture-backlog__metric"
              aria-label={`${readyToPostLabel}: ${formatCaptureTooltipCounts(
                readyToPostFiles,
                readyToPostTransactions
              )}`}
            >
              <span className="entity-capture-backlog__label">
                {t('organisation.capture-metric-post')}
              </span>
              <CaptureWorkflowReadyToPostIcon />
              <span className="entity-capture-backlog__value">
                {formatDisplayInteger(readyToPostFiles, i18n.language)}
              </span>
            </span>
          ) : null}
        </span>
      </Link>
    </StyledTooltip>
  );
};
